import React from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Music2, X } from 'lucide-react';
import { Button } from './ui/button';

// Memoize to prevent re-renders when other queue items change
const SortableQueueItem = React.memo(({ track, index, onRemove, onLoadToDeck }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: track.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // HTML5 drag for cross-component drag (queue → deck)
  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({ track, index }));
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      draggable
      onDragStart={handleDragStart}
      data-testid={`queue-track-${index}`}
      className="relative flex items-center gap-2 bg-dark-input/50 rounded-lg p-2 group hover:bg-dark-input/70 transition-colors cursor-move"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-purple-400 hover:text-sunset-orange focus:outline-none flex-shrink-0"
      >
        <GripVertical className="w-3 h-3" />
      </button>
      <span className="text-purple-400 font-mono text-xs w-5 flex-shrink-0">{index + 1}</span>
      <Music2 className="w-3 h-3 text-purple-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{track.title}</p>
        <p className="text-sunset-orange/60 text-xs truncate">{track.artist}</p>
      </div>
      
      {/* Deck A/B buttons - OVERLAY on hover */}
      <div className="absolute inset-0 bg-dark-card/95 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none group-hover:pointer-events-auto">
        <Button
          onClick={() => onLoadToDeck && onLoadToDeck(track, 'A')}
          size="sm"
          variant="outline"
          className="h-8 px-3 text-sm border-blue-500/50 text-blue-300 hover:bg-blue-500/20 hover:border-blue-500 font-semibold shadow-lg"
          title="Charger sur Deck A"
        >
          Deck A
        </Button>
        <Button
          onClick={() => onLoadToDeck && onLoadToDeck(track, 'B')}
          size="sm"
          variant="outline"
          className="h-8 px-3 text-sm border-green-500/50 text-green-300 hover:bg-green-500/20 hover:border-green-500 font-semibold shadow-lg"
          title="Charger sur Deck B"
        >
          Deck B
        </Button>
        <Button
          onClick={() => onRemove(track.id)}
          size="icon"
          variant="ghost"
          className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
});

const QueueManager = ({ queue, onReorder, onRemoveTrack, onLoadToDeck }) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = queue.findIndex(t => t.id === active.id);
      const newIndex = queue.findIndex(t => t.id === over.id);
      const newQueue = arrayMove(queue, oldIndex, newIndex);
      onReorder(newQueue);
    }
  };

  if (queue.length === 0) {
    return (
      <p className="text-sunset-orange/50 text-sm text-center py-4">
        Aucun titre en attente
      </p>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={queue.map(t => t.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2">
          {queue.map((track, idx) => (
            <SortableQueueItem
              key={track.id}
              track={track}
              index={idx}
              onRemove={onRemoveTrack}
              onLoadToDeck={onLoadToDeck}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

// Memoize to prevent unnecessary re-renders
export default React.memo(QueueManager);
